import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { API_URLS } from '../../constants/api-urls/api-urls';
import { skipSpinnerOptions } from '../../core/services/http-options.utils';

export interface CreateRoleplayFeedbackPayload {
    chat_session_id: number;
    rating: string;
    comment: string;
    business_unit_code: string;
    given_by: number;
}

export interface UpdateRoleplayFeedbackPayload {
    rating?: string;
    comment?: string;
    feedback_status?: string;
}

export interface RoleplayFeedbackActivityLogPayload {
    feedback_id: number;
    activity_type: string;
    description: string;
    old_status: string;
    new_status: string;
    comments: string;
    given_by: number;
    user_type: string;
    business_unit_code: string;
}

@Injectable({
    providedIn: 'root'
})
export class RoleplayFeedbackApiService {

    constructor(private http: HttpClient) { }

    getFeedbackList(businessUnitCode: string): Observable<any> {
        return this.http.get<any>(API_URLS.CONTACT_CENTER.ROLEPLAY_FEEDBACK_LIST(businessUnitCode));
    }

    createFeedback(payload: CreateRoleplayFeedbackPayload): Observable<any> {
        return this.http.post<any>(API_URLS.CONTACT_CENTER.CREATE_ROLEPLAY_FEEDBACK, payload);
    }

    updateFeedback(id: number, payload: UpdateRoleplayFeedbackPayload): Observable<any> {
        return this.http.put<any>(API_URLS.CONTACT_CENTER.UPDATE_ROLEPLAY_FEEDBACK(id), payload);
    }

    getFeedbackBySessionId(sessionId: number): Observable<any> {
        return this.http.get<any>(API_URLS.CONTACT_CENTER.GET_ROLEPLAY_FEEDBACK_BY_SESSION(sessionId));
    }

    getConversationsBySessionId(sessionId: number): Observable<any> {
        return this.http.get<any>(API_URLS.CONTACT_CENTER.GET_ROLEPLAY_CONVERSATIONS(sessionId));
    }

    updateRoleplayFeedbackActivityLog(payload: RoleplayFeedbackActivityLogPayload, file?: File | null): Observable<any> {
        const formData: FormData = new FormData();
        formData.append('feedback_id', String(payload.feedback_id));
        formData.append('activity_type', payload.activity_type || '');
        formData.append('description', payload.description || '');
        formData.append('old_status', payload.old_status || '');
        formData.append('new_status', payload.new_status || '');
        formData.append('comments', payload.comments || '');
        formData.append('given_by', String(payload.given_by));
        formData.append('user_type', payload.user_type || '');
        formData.append('business_unit_code', payload.business_unit_code || '');
        if (file) {
            formData.append('attachment', file, file.name);
        }
        return this.http.post<any>(API_URLS.CONTACT_CENTER.UPDATE_ROLEPLAY_FEEDBACK_ACTIVITY_LOG, formData);
    }

    fetchRoleplayFeedbackWithLogsByUser(filters?: { wwid?: number; feedback_status?: string; business_unit_code?: string; rating?: string; search?: string; limit?: number; offset?: number }): Observable<any> {
        let params = new HttpParams();
        if (filters) {
            if (filters.wwid) params = params.set('wwid', String(filters.wwid));
            if (filters.feedback_status) params = params.set('feedback_status', filters.feedback_status);
            if (filters.business_unit_code) params = params.set('business_unit_code', filters.business_unit_code);
            if (filters.rating) params = params.set('rating', filters.rating);
            if (filters.search) params = params.set('search', filters.search);
            if (filters.limit !== undefined) params = params.set('limit', String(filters.limit));
            if (filters.offset !== undefined) params = params.set('offset', String(filters.offset));
        }
        return this.http.get<any>(API_URLS.CONTACT_CENTER.FETCH_ROLEPLAY_FEEDBACK_WITH_LOGS_BY_USER, { params, ...skipSpinnerOptions() });
    }

    getRoleplayFeedbackWithLogs(feedbackId: number): Observable<any> {
        return this.http.get<any>(API_URLS.CONTACT_CENTER.ROLEPLAY_FEEDBACK_WITH_LOGS(feedbackId));
    }
}
